// What a subscription to the app costs, and what the words mean.
//
// This file is vocabulary only — constants, ids and one date helper. The
// decision that reads them is domain/entitlement.js, and nothing here knows
// about Stripe, Firestore or React. It is imported by the client (to show a
// price), by the Cloud Functions (to check the Stripe price), and by the admin
// CLI (to name a plan), which is why it holds no I/O: three callers that
// share the words cannot disagree about the price.
//
// ── The price ───────────────────────────────────────────────────────────────
//
//   30 days free, no card. Then $6.99 a month for every studio. One price, no
//   Stripe subscription schedule, nothing that changes by itself.
//
// Stored fields of the stepped plan (#722) — `plan: 'intro'`,
// `introInvoicesUsed`, a `grandfather` grant — stay readable on the accounts
// that carry them and do not change what anybody pays.

// ─── Money ──────────────────────────────────────────────────────────────────

// Minor units, like every other amount in this app (domain/money.js). $6.99,
// every month, for every studio. Stripe charges it through one price id
// (functions/billing/stripe.js says which variable holds it, and refuses a
// price whose amount is not this one).
export const MONTHLY_PRICE_MINOR = 699;

// One currency, because there is one price list. A studio in Tokyo pays USD.
// domain/locale.js derives the currency a *studio* invoices its families in,
// which is a different question and stays a different question.
export const BILLING_CURRENCY = 'USD';

// ─── Time ───────────────────────────────────────────────────────────────────

// Thirty days from signup, no card asked for. The trial ends in a paywall rather
// than in a charge, which is why nothing here needs to know about a card.
//
// An account keeps the `trialEndsOn` it was stamped with
// (functions/billing/account.js makes it immutable), so this number changes
// only the trial of an account stamped after it.
export const TRIAL_DAYS = 30;

// How long access outlives a payment that did not go through.
//
// The length of Stripe's own dunning window: a failed card is retried for
// about two weeks, and access lasts while it is. A *cancellation* gets no
// grace: access runs to the end of the paid period and stops.
export const PAST_DUE_GRACE_DAYS = 14;

// How long access outlives the end of a paid period on a subscription Stripe
// still calls `active`.
//
// A different window from the one above, for a different case.
// `PAST_DUE_GRACE_DAYS` covers a card that has *failed*.
// This one covers the gap where nothing has failed and nothing has been paid
// yet either: Stripe rolls `current_period_end` forward the moment the period
// ends, then finalises the renewal invoice and attempts it — about an hour
// later, and longer if a webhook delivery is slow or an outage delays one. In
// that gap `paidThroughOn` is yesterday, the subscription is healthy, and
// without this the fold would report `lapsed` and lock out a studio whose card
// is about to be charged without complaint.
//
// Three days rather than fourteen, because this is a delivery margin and not a
// dunning window. A renewal that genuinely fails does not sit here: Stripe
// moves the subscription to `past_due` within hours, which starts the fourteen
// days above from the last period actually PAID for — so a frozen card gets
// its fortnight and not a fortnight plus a month. A subscription that goes
// straight to `unpaid` or `canceled` gets no grace from either constant.
export const RENEWAL_GRACE_DAYS = 3;

// ─── The plans ──────────────────────────────────────────────────────────────

// What a studio is on. Stored on the account document and never inferred.
//
// Four ids, and two prices (#722): `comp` (free) and everything else (the
// monthly price). `intro` and `grandfathered` are read because stored accounts
// carry them. A new account is `standard`.
export const PLANS = {
  // Carried by some accounts (#722). The same price as `standard`.
  intro: 'intro',
  // The monthly price. What every new account is stamped with.
  standard: 'standard',
  // A price held by hand. Nothing writes a new one. The plan is read; its
  // `priceMinor`, on the account or the grant, is not — such a studio is
  // quoted, and sold, the monthly price (`priceMinorFor`).
  grandfathered: 'grandfathered',
  // Free, indefinitely, until marked otherwise. No Stripe customer, no card, no
  // invoices — the account simply never needs one.
  comp: 'comp',
};

export const PLAN_IDS = Object.freeze(Object.values(PLANS));

// ─── Entitlement states ─────────────────────────────────────────────────────
//
// What `entitlementAsOf` returns, in the order it prefers them. A studio can
// satisfy several at once — a comped account inside its trial is both — and the
// fold reports the one that is *paying for the access*, which is what the
// Membership tab explains and the admin CLI reasons about.
export const STATES = {
  comped: 'comped',     // free indefinitely, by hand
  active: 'active',     // a live subscription that Stripe says is paid
  granted: 'granted',   // access from awarded months, not from a payment
  trial: 'trial',       // inside the thirty days
  grace: 'grace',       // payment failed, still in Stripe's retry window
  // No billing account has been written for this studio: not known, which is
  // not `lapsed` (see ENTITLED_STATES below).
  unknown: 'unknown',
  lapsed: 'lapsed',     // none of the above
};

export const STATE_IDS = Object.freeze(Object.values(STATES));

// Which states may use the app. The enforcement seam reads `canUse` off the
// fold rather than this set, but this is where the answer is decided:
// everything except `lapsed`.
//
// **`unknown` is on the list: the gate fails open.** A studio with no billing
// account is one there is no information about — between signing up and a
// stamping path running, on a deployment without the callable, or for as long
// as a stamp keeps failing. Treating "not known" as "no" would lock a teacher
// out of their calendar because a write was slow.
export const ENTITLED_STATES = Object.freeze([
  STATES.comped, STATES.active, STATES.granted, STATES.trial, STATES.grace,
  STATES.unknown,
]);

// ─── Grants ─────────────────────────────────────────────────────────────────
//
// Every by-hand change to an account is one of these, appended and never
// edited. The reason a month was given survives the month.
export const GRANT_KINDS = {
  award: 'award',              // N months of access, by hand
  comp_start: 'comp_start',    // free indefinitely, from this date
  comp_end: 'comp_end',        // and no longer
  // A plan and a price, held by hand. Nothing writes a new one (#722); still
  // folded, so a grant already in a log still names its plan. Its price moves
  // nothing (`priceMinorFor`).
  grandfather: 'grandfather',
  // Which Stripe mode this studio's money goes through. **Neither changes any
  // entitlement** — `foldGrants` ignores both. The flag the code reads lives on
  // the account document (`isTestStudio`); these are the grant log's
  // permanent record of who set it, when, and why.
  test_studio_on: 'test_studio_on',
  test_studio_off: 'test_studio_off',
  // Whether this studio may send any email or text at all (#727). The same
  // arrangement as the pair above — ignored by `foldGrants`, the flag itself
  // (`outboundBlocked`, see ./outbound.js) on the account document, and these
  // the record of who barred or freed it, when, and why.
  outbound_blocked_on: 'outbound_blocked_on',
  outbound_blocked_off: 'outbound_blocked_off',
};

export const GRANT_KIND_IDS = Object.freeze(Object.values(GRANT_KINDS));

// The most months a single award may carry.
//
// A guard against a typo in the admin CLI, the only writer: `award someone
// 120` would be a decade of free access. Awarding more is two commands.
export const MAX_AWARD_MONTHS = 12;

// ─── Dates ──────────────────────────────────────────────────────────────────

// N months after a date-only string, on the UTC calendar grid.
//
// Here rather than in domain/recurrence.js because it answers a different
// question. `addDays` steps a fixed distance; this one lands on "the same day
// next month", which sometimes does not exist. The 31st of January plus one
// month has no 31st of February to land on, and the two reasonable answers are
// the 3rd of March (what `Date` does if you let it roll) and the 28th (what a
// person means). This clamps to the end of the target month, as Stripe does
// with an anchor of the 31st.
export function addMonths(iso, n) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!m || !Number.isFinite(Number(n))) return null;

  const year = Number(m[1]);
  const monthIndex = Number(m[2]) - 1;
  const day = Number(m[3]);
  const months = Math.trunc(Number(n));

  // Day 0 of the month *after* the target is the last day of the target, which
  // is how February gets 28 or 29 without a leap-year table — the same trick
  // domain/clock.js `endOfMonth` uses, for the same reason.
  const lastDay = new Date(Date.UTC(year, monthIndex + months + 1, 0)).getUTCDate();
  const d = new Date(Date.UTC(year, monthIndex + months, Math.min(day, lastDay)));

  const pad = (v) => String(v).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

// What one invoice costs, given the plan: nothing on a comp, and the monthly
// price on every other plan — `intro`, `standard` and `grandfathered` alike.
//
// **A price held by hand is not read** (#722). A `grandfather` grant or an
// account `priceMinor` may hold one ($4.99, or 0), but checkout sells the one
// monthly price to every plan that is not comped (billing/callables.js
// `startCheckout`), so quoting the held figure would put "no charge" beside a
// button that charges $6.99. The fold still reads the grant's plan; neither
// field moves the price. A subscription whose price was set by hand in Stripe
// is billed what Stripe holds; `npm run admin -- step-ups` lists every one not
// on the monthly price.
//
// A comp is 0 whatever plan the studio was on before, and the monthly price
// comes back the day the comp is ended.
export function priceMinorFor({ plan } = {}) {
  if (plan === PLANS.comp) return 0;
  return MONTHLY_PRICE_MINOR;
}
